/**
 * Position Code 生成工具类
 *
 * 根据后端验证规则生成 position code 的最后一段（hourOffset-checksum）
 *
 * 格式：level0.level1[.levelN].hourOffset-checksum
 * - hourOffset: 当前时间距离基准时间（2026-01-01 00:00:00）的小时数，使用标准 Base64 编码
 * - checksum: MD5(数据段 + hourOffset) 的标准 Base64 编码前 8 位
 */

import CryptoJS from 'crypto-js';

/**
 * 基准时间：2026-01-01 00:00:00 UTC
 */
const BASE_DATETIME = new Date('2026-01-01T00:00:00Z');

/**
 * 将长整型数值转换为 Base64 编码字符串
 * @param value 长整型数值
 * @returns Base64 编码的字符串（无填充）
 */
function longToBase64(value: number): string {
    // 创建 8 字节数组来存储 long 值
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setBigInt64(0, BigInt(value), false); // big-endian

    // 转换为 Uint8Array 以便处理字节
    const bytes = new Uint8Array(buffer);

    // 删除前面连续为0的字节
    let startIndex = 0;
    while (startIndex < bytes.length && bytes[startIndex] === 0) {
        startIndex++;
    }

    // 截取非零开头的字节数组
    const trimmedBytes = bytes.slice(startIndex);

  // 使用标准 Base64 编码（无填充，与 Java Base64.getEncoder().withoutPadding() 一致）
  const base64 = btoa(String.fromCharCode(...trimmedBytes))
    .replace(/=+$/, ''); // 只移除填充，保留 + 和 /

  return base64;
}

/**
 * 计算校验值
 * @param dataParts 数据段数组
 * @param hourOffset 小时偏移量
 * @returns 校验字符串（8位）
 */
function calculateChecksum(dataParts: string[], hourOffset: number): string {
  // 拼接数据段 + 小时偏移量
  const data = dataParts.join('.') + '.' + hourOffset;

  // 使用 crypto-js 计算 MD5
  const hash = CryptoJS.MD5(data);

  // 转换为标准 Base64 编码（无填充，与 Java Base64.getEncoder().withoutPadding() 一致）
  const base64 = hash.toString(CryptoJS.enc.Base64)
    .replace(/=+$/, ''); // 只移除填充，保留 + 和 /

  // 返回前 8 位
  return base64.substring(0, 8);
}

/**
 * 获取当前时间距离基准时间的小时数
 * @returns 小时数
 */
function getCurrentHourOffset(): number {
  const now = new Date();
  const diffMs = now.getTime() - BASE_DATETIME.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60));
}

/**
 * 生成完整的 position code
 *
 * @param dataParts 数据段数组（如 ['appCode', 'level1', 'level2']）
 * @returns 完整的 position code
 */
export function generatePositionCode(pc: string): string {
  const dataParts: string[] = pc.split('.');
  if (!dataParts || dataParts.length === 0) {
    throw new Error('Data parts cannot be empty');
  }

  // 计算小时偏移量
  const hourOffset = getCurrentHourOffset();

  // 计算校验和
  const checksum = calculateChecksum(dataParts, hourOffset);

  // 将小时偏移量编码为 Base64
  const hourOffsetBase64 = longToBase64(hourOffset);

  // 组装 position code
  return [...dataParts, `${hourOffsetBase64}-${checksum}`].join('.');
}
